import { ChangeDetectionStrategy, Component, computed, contentChild, input, output } from '@angular/core';

import { CadsFieldCaptionSlotDirective } from './cads-field-caption-slot.directive';
import type { CadsFieldConfig } from './cads-field.config';

/**
 * Form-kit chrome wrapper: renders an optional `label`, projects the control body via `<ng-content />`, optional
 * `[cadsFieldCaption]` projection (or `helperText` string), and an `errorMessage` caption below. Used internally by
 * form-kit controls so label/error/helper visuals stay consistent.
 *
 * Consumers normally don't reach for `<cads-field>` directly — pass `label` / `helperText` / `errorMessage` on a
 * specific form-kit component (`cads-input`, `cads-textarea`, etc.), or project `[cadsFieldCaption]` for interactive
 * caption markup.
 */
@Component({
  selector: 'cads-field',
  standalone: true,
  templateUrl: './cads-field.component.html',
  styleUrl: './cads-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'cads-field-host',
  },
})
export class CadsFieldComponent {
  readonly config = input.required<CadsFieldConfig>();
  /** Toggled by the projected control so the chrome can paint focus styling. */
  readonly focused = input<boolean>(false);
  /** Emitted with the optional label-adjacent info link when it is activated. */
  readonly infoLinkTapped = output<HTMLElement>();

  private readonly _captionSlot = contentChild(CadsFieldCaptionSlotDirective);

  protected readonly _hasProjectedCaption = computed(() => this._captionSlot() !== undefined);

  protected readonly _hasError = computed(() => (this.config().errorMessage?.trim() ?? '').length > 0);

  protected readonly _showHelper = computed(() => {
    if (this._hasError()) {
      return false;
    }
    if (this._hasProjectedCaption()) {
      return true;
    }
    return (this.config().helperText?.trim() ?? '').length > 0;
  });

  protected readonly _showCaption = computed(() => this._hasError() || this._showHelper());

  protected readonly _showProjectedCaption = computed(() => this._hasProjectedCaption() && !this._hasError());

  protected readonly _captionText = computed(() => {
    if (this._hasError()) {
      return (this.config().errorMessage ?? '').trim();
    }
    return this.config().helperText?.trim() ?? '';
  });

  protected readonly _captionId = computed(() => {
    const fromConfig = this.config().captionId?.trim();
    if (fromConfig) {
      return fromConfig;
    }
    return `${this.config().controlId}-caption`;
  });

  protected _onInfoLinkClick(event: MouseEvent): void {
    if (event.currentTarget instanceof HTMLElement) {
      this.infoLinkTapped.emit(event.currentTarget);
    }
  }
}
